import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldX, CheckCircle2, XCircle, Clock, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";

export default function ReferenceResponse() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/references/respond/${token}`);
        setInvitation(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation not found or has expired.');
      } finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handleRespond = async (action) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/references/respond/${token}`, { action, comment });
      setResult(action);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit response.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-meritum-teal" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-meritum-navy to-meritum-teal/90 flex items-center justify-center p-4" data-testid="reference-response-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-meritum-navy p-6 text-center">
          <Link to="/"><img src={LOGO_URL} alt="MeritumPraxis" className="h-12 mx-auto mb-2" /></Link>
          <p className="text-white/60 font-body text-xs">Progress Made Visible</p>
        </div>

        <div className="p-8">
          {error ? (
            <div className="text-center" data-testid="reference-error">
              <XCircle className="mx-auto text-red-400" size={48} />
              <h2 className="font-heading text-xl font-semibold text-meritum-navy mt-4">{error}</h2>
              <Link to="/" className="mt-6 inline-block"><Button className="bg-meritum-teal text-white hover:bg-meritum-navy font-body">Go to Homepage</Button></Link>
            </div>
          ) : result ? (
            <div className="text-center" data-testid="reference-success">
              {result === 'confirmed' ? (
                <>
                  <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
                  <h2 className="font-heading text-xl font-bold text-meritum-navy mt-4">Reference Confirmed</h2>
                  <p className="font-body text-slate-600 mt-2">Thank you for confirming <strong>{invitation.candidate_name}</strong>'s skill in <strong>"{invitation.skill_name}"</strong>.</p>
                </>
              ) : (
                <>
                  <XCircle className="mx-auto text-red-400" size={56} />
                  <h2 className="font-heading text-xl font-bold text-meritum-navy mt-4">Reference Declined</h2>
                  <p className="font-body text-slate-600 mt-2">You have declined the reference request for <strong>"{invitation.skill_name}"</strong>.</p>
                </>
              )}
              <p className="font-body text-sm text-slate-400 mt-4">The candidate has been notified of your response.</p>
              <Link to="/" className="mt-6 inline-block"><Button className="bg-meritum-teal text-white hover:bg-meritum-navy font-body">Visit MeritumPraxis</Button></Link>
            </div>
          ) : invitation.status !== 'pending' ? (
            <div className="text-center" data-testid="reference-already-responded">
              <Clock className="mx-auto text-amber-400" size={48} />
              <h2 className="font-heading text-xl font-semibold text-meritum-navy mt-4">Already Responded</h2>
              <p className="font-body text-slate-600 mt-2">This reference request has already been <strong>{invitation.status}</strong>.</p>
            </div>
          ) : (
            <div data-testid="reference-form">
              <div className="text-center mb-6">
                <ShieldCheck className="mx-auto text-meritum-teal" size={40} />
                <h2 className="font-heading text-xl font-bold text-meritum-navy mt-3">Reference Verification</h2>
              </div>
              <div className="bg-slate-50 rounded-xl p-5 mb-6 space-y-3">
                <div className="flex justify-between">
                  <span className="font-body text-sm text-slate-500">Candidate</span>
                  <span className="font-body text-sm font-semibold text-meritum-navy" data-testid="ref-candidate-name">{invitation.candidate_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-sm text-slate-500">Skill</span>
                  <span className="font-body text-sm font-semibold text-meritum-gold" data-testid="ref-skill-name">{invitation.skill_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-sm text-slate-500">Referee</span>
                  <span className="font-body text-sm text-meritum-navy" data-testid="ref-referee-name">{invitation.referee_name}</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="font-body text-sm font-medium text-meritum-navy flex items-center gap-2 mb-2">
                  <MessageSquare size={14} className="text-slate-400" />Comment (optional)
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment about this candidate's skill..."
                  rows={3}
                  className="resize-none"
                  data-testid="ref-comment-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleRespond('confirmed')}
                  disabled={submitting}
                  className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 font-body font-bold"
                  data-testid="confirm-reference-btn"
                >
                  <CheckCircle2 size={18} className="mr-2" />
                  {submitting ? 'Submitting...' : 'Confirm'}
                </Button>
                <Button
                  onClick={() => handleRespond('declined')}
                  disabled={submitting}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 font-body font-bold"
                  data-testid="decline-reference-btn"
                >
                  <XCircle size={18} className="mr-2" />Decline
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
